<script lang="ts" setup>
    import VueHorizontal from "vue-horizontal";
    import RothcoCta from "@/components/RothcoCta.vue";
    import {
        useRichTextFormatting,
        richTextHasText
    } from "@/helpers/composables/builderFieldUtility.ts";
    import type { Cta } from "@/helpers/builder-init/index.ts";
    import type { Text, Media } from "@/helpers/interfaces/BuilderFields.ts";
    import { type PropType, ref, computed } from "vue";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    interface MediaWithUrl extends Media {
        url: string;
    }

    let props = defineProps({
        heading: {
            type: Object as PropType<Text>,
            default() {
                return {};
            }
        },
        description: {
            type: Object as PropType<Text>,
            default() {
                return {};
            }
        },
        cta: {
            type: Object as PropType<Cta>,
            default() {
                return { type: "link" };
            }
        },
        image_content: {
            type: Array as PropType<MediaWithUrl[]>,
            default() {
                return [{}, {}, {}, {}]; // min. 4 images
            }
        }
    });

    let getTextColorClass = (color) => `text-${color}`;

    let carouselRef = ref(null);

    const ctaClasses = computed(() => {
        let classes = [props.cta.style ? props.cta.style : "primary"];
        if (props.cta.size != "default") classes.push(props.cta.size);
        return classes;
    });
</script>
<template>
    <section class="ugc-component">
        <!-- HEADER -->
        <span
            v-if="props.heading.text"
            :class="['headline-xl', getTextColorClass(props.heading.color)]"
            role="heading"
            aria-level="3"
            >{{ props.heading.text }}</span
        >
        <div
            id="description"
            v-if="richTextHasText(props.description.text)"
            v-html="useRichTextFormatting(props.description.text)"
            :class="[getTextColorClass(props.description.color)]"
            role="heading"
            aria-level="4"
        ></div>
        <RothcoCta
            v-if="props.cta.label"
            :ctaType="props.cta.type"
            :class="[...ctaClasses]"
            v-bind="getCtaLink(props.cta)"
            :aria-label="props.cta.aria_label"
        >
            {{ props.cta.label }}
        </RothcoCta>
        <!-- CAROUSEL -->
        <div
            v-show="carouselRef?.hasPrev"
            class="arrow-prev"
            @click="carouselRef.prev($event)"
            aria-label="Scroll left"
            tabindex="0"
            role="button"
        ></div>
        <vue-horizontal ref="carouselRef" responsive class="ugc-carousel"
            role="region"
            :aria-label="heading.text || 'Carousel'"
        >
            <a
                v-for="(image, index) in props.image_content"
                :key="index"
                :href="image.url"
                class="pti-item"
                :aria-label="image.aria_label"
                tabindex="0"
            >
                <img
                    loading="lazy"
                    :src="image.image"
                    :alt="image.alt_text"
                    class="pti-image"
                />
            </a>
            <template v-slot:btn-next><span aria-hidden="true"></span></template>
            <template v-slot:btn-prev><span aria-hidden="true"></span></template>
        </vue-horizontal>
        <div
            v-show="carouselRef?.hasNext"
            class="arrow-next"
            @click="carouselRef.next($event)"
            aria-label="Scroll right"
            tabindex="0"
            role="button"
        ></div>
    </section>
</template>
